import { Interval } from "@digits-graphql/admin/graphql-bearer"
import {
  createRoutes,
  type RouteConfig,
  StaticRoutes,
} from "@digits-shared/components/Router/DigitsRoute"

class AdminStaticRoutes extends StaticRoutes {
  root = "/"
  login = "/login"
  logout = "/logout"

  userDetails = "/users/:userId"
  signUpDetails = "/signups/:signupId"

  organizationDetails: RouteConfig = {
    parameterizedPath: "/organizations/:organizationId",
    moduleName: "Organization Details",
  }

  organizationSupport: RouteConfig = {
    parentRoute: this.organizationDetails,
    parameterizedPath: "/support",
    moduleName: "Organization Support",
  }

  organizationEmployees: RouteConfig = {
    parentRoute: this.organizationSupport,
    parameterizedPath: "/employees",
    moduleName: "Organization Employees",
  }

  organizationBilling: RouteConfig = {
    parentRoute: this.organizationSupport,
    parameterizedPath: "/billing",
    moduleName: "Organization Billing",
  }

  organizationMaintenance: RouteConfig = {
    parentRoute: this.organizationSupport,
    parameterizedPath: "/maintenance",
    moduleName: "Organization Maintenance",
  }

  organizationAspects: RouteConfig = {
    parentRoute: this.organizationSupport,
    parameterizedPath: "/aspects",
    moduleName: "Organization Aspects",
  }

  organizationPackage: RouteConfig = {
    parentRoute: this.organizationSupport,
    parameterizedPath: "/package",
    moduleName: "Organization Package",
  }

  organizationChartOfAccountsTemplates: RouteConfig = {
    parentRoute: this.organizationSupport,
    parameterizedPath: "/coa-templates",
    moduleName: "Organization CoA Templates",
  }

  legalEntityDetails: RouteConfig = {
    parameterizedPath: "/legal-entities/:legalEntityId",
    moduleName: "Legal Entity Details",
  }

  legalEntityParties: RouteConfig = {
    parentRoute: this.legalEntityDetails,
    parameterizedPath: "/parties",
    moduleName: "Legal Entity Parties",
  }

  legalEntityPartiesByTypeAndDesignation: RouteConfig = {
    parentRoute: this.legalEntityParties,
    parameterizedPath: "/type/:type/designation/:designation",
    moduleName: "Legal Entity Parties",
  }

  legalEntityPrefixes: RouteConfig = {
    parentRoute: this.legalEntityDetails,
    parameterizedPath: "/prefixes",
    moduleName: "Legal Entity Prefixes",
  }

  legalEntityPrefixesPotential: RouteConfig = {
    parentRoute: this.legalEntityPrefixes,
    parameterizedPath: "/potential",
    moduleName: "Legal Entity Prefixes - Potential",
  }

  legalEntityPrefixesConfirmed: RouteConfig = {
    parentRoute: this.legalEntityPrefixes,
    parameterizedPath: "/confirmed",
    moduleName: "Legal Entity Prefixes - Confirmed",
  }

  legalEntitySupport: RouteConfig = {
    parentRoute: this.legalEntityDetails,
    parameterizedPath: "/support",
    moduleName: "Legal Entity Support",
  }

  legalEntityInvoiceMigrations: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/invoice-migrations",
    moduleName: "Legal Entity Invoice Migrations",
  }

  legalEntityInvoiceMigrationDetail: RouteConfig = {
    parentRoute: this.legalEntityInvoiceMigrations,
    parameterizedPath: "/:type/:timestamp",
    moduleName: "Legal Entity Invoice Migration Detail",
  }

  legalEntityBillMigrations: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/bill-migrations",
    moduleName: "Legal Entity Bill Migrations",
  }

  legalEntityBillMigrationDetail: RouteConfig = {
    parentRoute: this.legalEntityBillMigrations,
    parameterizedPath: "/:type/:timestamp",
    moduleName: "Legal Entity Bill Migration Detail",
  }

  legalEntityBillSourceBackfills: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/bill-source-backfills",
    moduleName: "Legal Entity Bill Source Backfills",
  }

  legalEntityBillSourceBackfillDetail: RouteConfig = {
    parentRoute: this.legalEntityBillSourceBackfills,
    parameterizedPath: "/:type/:timestamp",
    moduleName: "Legal Entity Bill Source Backfill Detail",
  }

  legalEntityAuditFacts: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/audit-facts",
    moduleName: "Legal Entity Audit Facts",
  }

  legalEntityAuditFactDetail: RouteConfig = {
    parentRoute: this.legalEntityAuditFacts,
    parameterizedPath: "/:factId",
    moduleName: "Legal Entity Audit Fact Detail",
  }

  legalEntityLedgerBillEmailHistory: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/ledger-bill-email-history",
    moduleName: "Legal Entity LedgerBill Email History",
  }

  legalEntityClassificationEvents: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/classification-events",
    moduleName: "Legal Entity Classification Events",
  }

  legalEntityValidation: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/validation",
    moduleName: "Legal Entity Validation",
  }

  legalEntityEmployees: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/employees",
    moduleName: "Legal Entity Employees",
  }

  legalEntityBookClosed: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/book-closed",
    moduleName: "Legal Entity Books Closed",
  }

  legalEntityMaintenance: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/maintenance",
    moduleName: "Legal Entity Maintenance",
  }

  legalEntityDataSourceJobErrors: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/datasourcejob/errors",
    moduleName: "Legal Entity Datasource Errors",
  }

  legalEntityReports: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/reports",
    moduleName: "Legal Entity Reports",
    timeParameterOptions: {
      interval: Interval.Month,
    },
  }

  legalEntityReportValidation: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/report/validation/:reportId/:versionId",
    moduleName: "Legal Entity Report Validation",
  }

  legalEntityConnections: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/connections",
    moduleName: "Legal Entity Connections",
  }

  legalEntityConnection: RouteConfig = {
    parentRoute: this.legalEntityConnections,
    parameterizedPath: "/:id",
    moduleName: "Legal Entity Connection",
  }

  legalEntityCategories: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/categories",
    moduleName: "Legal Entity Categories",
  }

  legalEntityQBOLineClasses: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/qbo-lineclass",
    moduleName: "Legal Entity QBO Line Classes",
  }

  legalEntityPayrollProviders: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/payroll-providers",
    moduleName: "Legal Entity Payroll Providers",
  }

  legalEntityAgentParties: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/agent-parties",
    moduleName: "Legal Entity Agent Parties",
  }

  legalEntityPartyExtractions: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/party-extractions",
    moduleName: "Legal Entity Party Extractions",
  }

  legalEntityPartyMatchSets: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/party-match-sets",
    moduleName: "Legal Entity Party Match Sets",
  }

  legalEntityCSVImports: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/csv-imports",
    moduleName: "Legal Entity CSV Imports",
  }

  legalEntityScheduleExtractions: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/schedule-extractions",
    moduleName: "Legal Entity Schedule Extractions",
  }

  legalEntityScheduleExtractionDocument: RouteConfig = {
    parentRoute: this.legalEntityScheduleExtractions,
    parameterizedPath: "/:documentId",
    moduleName: "Legal Entity Schedule Extraction Document",
  }

  legalEntityQBODepartments: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/qbo-departments",
    moduleName: "Legal Entity QBO Departments",
  }

  legalEntityReconciliations: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/reconciliations",
    moduleName: "Legal Entity Reconciliations",
  }

  legalEntityAdyenStatus: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/adyen-status",
    moduleName: "Legal Entity Adyen Status",
  }

  legalEntityAdyenTransfers: RouteConfig = {
    parentRoute: this.legalEntityAdyenStatus,
    parameterizedPath: "/transfers",
    moduleName: "Legal Entity Adyen Transfers",
  }

  legalEntityAspects: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/aspects",
    moduleName: "Legal Entity Aspects",
  }

  legalEntityViewDataExplorer: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/view-data-explorer",
    moduleName: "Explore View Data",
  }

  legalEntityWorkflows: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/workflows",
    moduleName: "Legal Entity Workflows",
  }

  legalEntityWorkflowDetail: RouteConfig = {
    parentRoute: this.legalEntityWorkflows,
    parameterizedPath: "/:workflowId",
    moduleName: "Legal Entity Workflow Detail",
  }

  additionalServices = "/additional-services"

  stuckLegalEntities = `${this.additionalServices}/stuck-legal-entities`

  legalEntities = `${this.additionalServices}/legal-entities`
  legalEntitiesByStatus = `${this.additionalServices}/legal-entities/:status`

  organizations = `${this.additionalServices}/organizations`
  accountantOrganizations = `${this.organizations}/accountants`
  sandboxOrganizations = `${this.organizations}/sandboxes`
  developerOrganizations = `${this.organizations}/developers`
  allOrganizations = `${this.organizations}/all`

  parties = `${this.additionalServices}/parties`
  partiesGlobal = `${this.parties}/global`
  partiesPrefixes = `${this.parties}/prefixes`
  partiesPrefixesPotential = `${this.partiesPrefixes}/potential`
  partiesPrefixesConfirmed = `${this.parties}/prefixes/confirmed`
  partiesPrefixesIgnored = `${this.parties}/prefixes/ignored`

  institutions = `${this.additionalServices}/institutions`
  institutionDetails = `${this.institutions}/:id`

  doppelgangerPermits = `${this.additionalServices}/permits`

  transactions = `${this.additionalServices}/transactions`
  transactionsDetails = `${this.transactions}/:id`

  workItems = `${this.additionalServices}/work-items`
  workItemDetails = `${this.workItems}/:id`

  sandbox = `${this.additionalServices}/sandbox`

  superusers = `${this.additionalServices}/superusers`

  design = `${this.additionalServices}/design`

  email = `${this.additionalServices}/email`
  emailTemplates = `${this.additionalServices}/emailTemplates`
  emailTemplate = `${this.emailTemplates}/:template`
  emailDelivery = `${this.email}/delivery`

  chartsOfAccounts = `${this.additionalServices}/coa`
  chartsOfAccountsChart = `${this.chartsOfAccounts}/:id`
  chartsOfAccountsTemplate = `${this.chartsOfAccounts}/:templateId/:organizationId`

  scheduleTemplates = `${this.additionalServices}/schedule-templates`
  scheduleTemplateDetail = `${this.scheduleTemplates}/:scheduleTemplateId`
  scheduleTemplateSpecialization = `${this.scheduleTemplateDetail}/specializations/:specializationId`

  scheduleCategoryHints = `${this.additionalServices}/schedule-category-hints`

  validation = `${this.additionalServices}/validation`
  validationStats = `${this.validation}/stats`

  databaseMigrations = `${this.additionalServices}/database-migrations`
  databaseMigration = `${this.databaseMigrations}/:prNumber`

  emailPreferences = `${this.additionalServices}/email-preferences`

  localTools = `${this.additionalServices}/local-tools`
  quickbooksXmlViewer = `${this.localTools}/quickbooks-xml-viewer`
  developerPortal = `${this.localTools}/developer-portal`

  machineLearning = `${this.additionalServices}/machine-learning`
  embeddingSearch = `${this.additionalServices}/embedding-search`
  connect = `${this.additionalServices}/connect`
  connectAppDetails = `${this.connect}/:connectorId`
  partners = `${this.additionalServices}/partners`
  tierDetails = `${this.partners}/:stripeId`

  documents = `${this.additionalServices}/documents`

  adyenDashboard = `${this.additionalServices}/adyen-dashboard`

  pipelineDefinitions = `${this.additionalServices}/pipeline-definitions`

  paymentJournalEntries = `${this.additionalServices}/payment-journal-entries`
  paymentJournalEntriesByPaymentIntentId = `${this.paymentJournalEntries}/payment-intent-id/:paymentIntentId`
  paymentJournalEntriesByPaymentId = `${this.paymentJournalEntries}/payment-id/:paymentId`
  paymentJournalEntriesByPayoutIntentId = `${this.paymentJournalEntries}/payout-intent-id/:payoutIntentId`
  paymentJournalEntriesByPayoutId = `${this.paymentJournalEntries}/payout-id/:payoutId`

  paymentsLedgerCoA = `${this.additionalServices}/payments-ledger-coa`
  paymentsLedgerCoAByOwner = `${this.paymentsLedgerCoA}/:ownerId`
  paymentsLedgerCoACategoryEntries = `${this.paymentsLedgerCoAByOwner}/:canonicalCategoryLabel`
  paymentsLedgerCoATagValueEntries = `${this.paymentsLedgerCoAByOwner}/tag-value/:tagValue`

  paymentPayoutChain = `${this.additionalServices}/payment-payout-chain`
  paymentPayoutChainByPaymentId = `${this.paymentPayoutChain}/payment-id/:paymentId`
  paymentPayoutChainByPaymentIntentId = `${this.paymentPayoutChain}/payment-intent-id/:paymentIntentId`
  paymentPayoutChainByPayoutId = `${this.paymentPayoutChain}/payout-id/:payoutId`
  paymentPayoutChainByPayoutIntentId = `${this.paymentPayoutChain}/payout-intent-id/:payoutIntentId`

  storybook = `${this.additionalServices}/storybook`

  auditBackfills = `${this.additionalServices}/audit-backfills`
  auditBackfill = `${this.auditBackfills}/:jobId`

  auditCapture = `${this.additionalServices}/audit-capture`
  auditCaptureBundle = `${this.auditCapture}/bundles/:partitionId/:bundleSequence`
  auditCaptureTransaction = `${this.auditCapture}/transaction`
  auditCaptureRecord = `${this.auditCapture}/record`

  globalWorkflows = `${this.additionalServices}/workflows`

  closedPeriodReviewQueue = `${this.additionalServices}/closed-period-review-queue`
}

export default createRoutes(AdminStaticRoutes)
